const ZOOM_PRESETS = [50, 75, 100, 125, 150, 200] as const;

export type EditorZoomControlsVariant = 'status' | 'mobile';

interface EditorZoomControlsProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onZoomFit: () => void;
  variant?: EditorZoomControlsVariant;
  /** Clamp range; defaults match mobile bottom bar. */
  min?: number;
  max?: number;
  step?: number;
}

function clampZoom(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Shared − / Fit-to-page dropdown / + for status bar and phone bottom board.
 */
export default function EditorZoomControls({
  zoom,
  onZoomChange,
  onZoomFit,
  variant = 'status',
  min = 25,
  max = 200,
  step = 10,
}: EditorZoomControlsProps) {
  const rounded = Math.round(zoom);
  const isPreset = (ZOOM_PRESETS as readonly number[]).includes(rounded);
  const selectValue = isPreset ? String(rounded) : `current:${rounded}`;

  const rootClass =
    variant === 'mobile' ? 'ps-mobile-zoom' : 'ps-zoom-controls';
  const btnClass = variant === 'mobile' ? 'ps-mobile-bottom-btn' : undefined;
  const selectClass =
    variant === 'mobile' ? 'ps-mobile-zoom-select' : 'ps-zoom-select';

  return (
    <div className={rootClass} role="group" aria-label="Zoom">
      {variant === 'mobile' && (
        <span className="ps-mobile-bottom-label">Zoom</span>
      )}
      <button
        type="button"
        className={btnClass}
        aria-label="Zoom out"
        onClick={() => onZoomChange(clampZoom(rounded - step, min, max))}
      >
        −
      </button>
      <label className={selectClass}>
        <span className="sr-only">Zoom level</span>
        <select
          value={selectValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'fit') {
              onZoomFit();
              return;
            }
            if (v.startsWith('current:')) return;
            const n = Number(v);
            if (!Number.isFinite(n)) return;
            onZoomChange(clampZoom(n, min, max));
          }}
          title="Zoom"
        >
          <option value="fit">Fit to page</option>
          {ZOOM_PRESETS.map((p) => (
            <option key={p} value={String(p)}>
              {p}%
            </option>
          ))}
          {!isPreset && (
            <option value={`current:${rounded}`}>{rounded}%</option>
          )}
        </select>
      </label>
      <button
        type="button"
        className={btnClass}
        aria-label="Zoom in"
        onClick={() => onZoomChange(clampZoom(rounded + step, min, max))}
      >
        +
      </button>
    </div>
  );
}
